import { useMemo } from "react";
import {
  queryOptions,
  useQueryClient,
  useQuery,
  useMutation,
  useInfiniteQuery,
} from "@tanstack/react-query";

import * as videosApi from "@app/services/videos";
import * as videoTemplatesApi from "@app/services/videoTemplates";
import {
  downloadFile,
  downloadImage,
  downloadImages,
  generateExportJson,
} from "@app/services/downloadFile";
import { createImageColorBlob } from "@app/utils/imageBlob";
import { buildModularExportJson } from "@app/utils/modularScenes";

import { presignURLQueryOptions, s3Key } from "@app/hooks/s3";
import { useExportBanners } from "@app/hooks/videoWizard";
import { useExpressTemplate } from "@app/hooks/expressTemplates";

export const videoKey = {
  all: ["videos"],
  lists: () => [...videoKey.all, "list"],
  list: (filters) => [...videoKey.lists(), "infinite", { filters }],
  details: () => [...videoKey.all, "detail"],
  detail: (id) => [...videoKey.details(), id],
  templates: () => [...videoKey.all, "templates"],
  template: (id) => [...videoKey.templates(), id],
};

export const getNewVideoPath = (videoId) =>
  videoId ? `/projects/new/${videoId}` : "/projects/new";

const selectVideos = (data) => data.pages.flatMap((page) => page.videos);

const updateVideoLists = (client, update, filters = {}) =>
  client.setQueriesData({ queryKey: videoKey.lists(), ...filters }, (data) =>
    data?.pages
      ? {
          ...data,
          pages: data.pages.map((page, index) => ({
            ...page,
            videos: update(page.videos, index),
          })),
        }
      : data,
  );

const restoreVideoLists = (client, previousLists = [], id) => {
  previousLists.forEach(([key, previous]) => {
    client.setQueryData(key, (data) =>
      data?.pages
        ? {
            ...data,
            pages: data.pages.map((page, index) => {
              const previousPage = previous?.pages?.[index];
              const previousIndex =
                previousPage?.videos.findIndex((video) => video.id === id) ??
                -1;
              // Roll back only this item so concurrent mutations stay intact.
              const videos = page.videos.filter((video) => video.id !== id);
              if (previousIndex >= 0) {
                videos.splice(
                  previousIndex,
                  0,
                  previousPage.videos[previousIndex],
                );
              }
              return { ...page, videos };
            }),
          }
        : data,
    );
  });
};

export const useVideos = (options = {}) => {
  const { user = "me", pageSize = 25, ...queryOptions } = options;
  const query = useInfiniteQuery({
    queryKey: videoKey.list({ user, pageSize }),
    queryFn: ({ pageParam, signal }) =>
      videosApi.getVideos(user, { signal }, { page: pageParam, pageSize }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages
        ? lastPage.pagination.page + 1
        : undefined,
    select: selectVideos,
    staleTime: 1 * 60 * 1000, // 1 minute
    meta: {
      errorMessage: "Failed to load projects. Please try again.",
    },
    ...queryOptions,
  });

  return query;
};

export const useVideo = (id, options = {}) => {
  const query = useQuery({
    queryKey: videoKey.detail(id),
    queryFn: ({ signal }) => videosApi.getVideo(id, { signal }),
    meta: {
      errorMessage: "Failed to load the project. Please try again.",
    },
    enabled: !!id,
    ...options,
  });

  return query;
};

export const useCreateVideo = () => {
  return useMutation({
    mutationKey: videoKey.detail("new"),
    mutationFn: (variables, { signal }) =>
      videosApi.createVideo(variables, { signal }),
    onMutate: async (variables, { client }) => {
      await client.cancelQueries({ queryKey: videoKey.lists() });

      const previousLists = client.getQueriesData({
        queryKey: videoKey.lists(),
      });
      updateVideoLists(
        client,
        (videos, index) => (index === 0 ? [variables, ...videos] : videos),
        { predicate: (query) => query.queryKey[3]?.filters?.user === "me" },
      );

      return { previousLists };
    },
    onSuccess: (data, variables, onMutateResult, { client }) => {
      client.setQueryData(videoKey.details(data.id), data);
    },
    onError: (error, variables, { previousLists } = {}, { client }) => {
      restoreVideoLists(client, previousLists, variables.id);
      client.removeQueries({
        queryKey: videoKey.detail(variables.id),
      });
    },
    onSettled: (data, error, variables, onMutateResult, { client }) => {
      client.invalidateQueries({ queryKey: videoKey.lists() });
    },
  });
};

export const useDuplicateVideo = () => {
  return useMutation({
    mutationKey: [...videoKey.all, "duplicate"],
    mutationFn: ({ id }) => videosApi.duplicateVideo(id),
    onSuccess: (data, variables, onMutateResult, { client }) => {
      client.setQueryData(videoKey.detail(data.id), data);
      client.invalidateQueries({ queryKey: videoKey.lists() });
    },
    meta: {
      errorMessage: "Failed to duplicate the project. Please try again.",
    },
  });
};

export const useUpdateVideo = (id) => {
  return useMutation({
    scope: {
      id: videoKey.detail(id).join("/"),
    },
    mutationKey: videoKey.detail(id),
    mutationFn: (variables, { signal }) =>
      videosApi.updateVideo(id ?? variables?.id, variables, { signal }),
    onMutate: async (variables, { client }) => {
      await client.cancelQueries({ queryKey: videoKey.all });

      const previousData = client.getQueryData(
        videoKey.detail(id ?? variables?.id),
      );
      const previousLists = client.getQueriesData({
        queryKey: videoKey.lists(),
      });
      client.setQueryData(videoKey.detail(id ?? variables?.id), {
        ...previousData,
        ...variables,
      });
      updateVideoLists(client, (videos) =>
        videos.map((v) =>
          v.id === (id ?? variables?.id) ? { ...v, ...variables } : v,
        ),
      );

      return { previousData, previousLists };
    },
    onSuccess: (data, variables, onMutateResult, { client }) => {
      client.setQueryData(videoKey.detail(id ?? variables?.id), data);
    },
    onError: (
      error,
      variables,
      { previousData, previousLists } = {},
      { client },
    ) => {
      console.warn("Error updating video:", error, variables);
      client.setQueryData(videoKey.detail(id ?? variables?.id), previousData);
      restoreVideoLists(client, previousLists, id ?? variables?.id);
    },
    onSettled: (data, error, variables, onMutateResult, { client }) => {
      client.invalidateQueries({ queryKey: videoKey.lists() });
      if (
        client.isMutating({
          mutationKey: videoKey.detail(id ?? variables?.id),
        }) === 1
      ) {
        client.invalidateQueries({
          queryKey: videoKey.detail(id ?? variables?.id),
        });
      }
    },
    meta: {
      errorMessage: "Failed to update the project.",
    },
  });
};

export const useDeleteVideo = () => {
  return useMutation({
    mutationKey: videoKey.lists(),
    mutationFn: (variables, { signal }) =>
      videosApi.deleteVideo(variables.id, { signal }),
    onMutate: async (variables, { client }) => {
      await client.cancelQueries({ queryKey: videoKey.lists() });

      const previousData = client.getQueryData(videoKey.detail(variables.id));
      const previousLists = client.getQueriesData({
        queryKey: videoKey.lists(),
      });

      updateVideoLists(client, (videos) =>
        videos.filter((v) => v.id !== variables.id),
      );
      client.removeQueries({ queryKey: videoKey.detail(variables.id) });

      return { previousData, previousLists };
    },
    onError: (
      error,
      variables,
      { previousLists, previousData } = {},
      { client },
    ) => {
      restoreVideoLists(client, previousLists, variables.id);
      client.setQueryData(videoKey.detail(variables.id), previousData);
    },
    onSettled: (data, error, variables, onMutateResult, { client }) => {
      client.invalidateQueries({ queryKey: videoKey.lists() });
    },
  });
};

export const useVideoTemplates = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: [...videoKey.templates(), { filters }],
    queryFn: ({ signal }) =>
      videoTemplatesApi.getVideoTemplates(filters, { signal }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: {
      errorMessage: "Failed to load video templates. Please try again.",
    },
    ...options,
  });
};

export const useVideoTemplate = (id, options = {}) => {
  return useQuery({
    queryKey: videoKey.template(id),
    queryFn: ({ signal }) => videoTemplatesApi.getVideoTemplate(id, { signal }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: {
      errorMessage: "Failed to load the video template. Please try again.",
    },
    enabled: !!id,
    ...options,
  });
};

const handleAssetDownload = async (asset, { client, signal }) => {
  if (asset.type === "color") {
    const color = asset.color ?? asset.value;
    if (!color) {
      throw new Error("No color to download");
    }
    const blob = await createImageColorBlob(color);
    return { blob, name: asset.name, contentType: "image/png" };
  }

  let url = asset.src;

  if (asset.filePath) {
    const queryKey = s3Key.presignURL(asset.filePath);
    const cancelPresignQuery = () => client.cancelQueries({ queryKey });
    signal?.addEventListener("abort", cancelPresignQuery);

    try {
      const s3Data = await client.query(presignURLQueryOptions(asset.filePath));
      url = s3Data.presignURL;
    } finally {
      signal?.removeEventListener("abort", cancelPresignQuery);
    }
  }

  return { url, name: asset.name };
};

export const useDownloadAsset = () => {
  return useMutation({
    mutationFn: handleAssetDownload,
    onSuccess: (data) => {
      if (data.blob) {
        return downloadFile(data.blob, data.name, data.contentType);
      }
      return downloadImage(data, data.name);
    },
  });
};

export const useVideoAssets = (videoId) => {
  const video = useVideo(videoId);
  const template = useVideoTemplate(video.data?.templateId);
  const assetsTemplate = useExpressTemplate(video.data?.assetsTemplateId);
  const exportBanners = useExportBanners(video.data?.id, {
    enabled: !!video.data?.templateAssetsRenditions?.filter(
      (rendition) => !!rendition?.id,
    ).length,
  });

  const assets = useMemo(() => {
    const orderedKeys = [
      ...(template.data?.assets ?? []).map((asset) => asset.name),
      ...(template.data?.verticals ?? []).flatMap((vertical) =>
        (vertical.assets ?? []).map((asset) => asset.name),
      ),
      ...(template.data?.imageVariations ?? [])
        .filter((asset) => !!asset.type)
        .map((asset) => asset.key),
    ];
    const uniqueOrderedKeys = [...new Set(orderedKeys.filter(Boolean))];

    const videoByName = new Map(
      (video.data?.assets ?? [])
        .filter((asset) => asset?.name)
        .map((asset) => [asset.name, asset]),
    );

    const baseAssets = uniqueOrderedKeys
      .map((key) => videoByName.get(key))
      .filter(Boolean);

    const renditionFileByPage = new Map(
      (video.data?.templateAssetsRenditionFiles ?? [])
        .filter((file) => file?.filePath)
        .map((file) => [file.pageNumber, file.filePath]),
    );

    const templateAssets =
      assetsTemplate.data?.pages?.flatMap((page) =>
        page.documentPages
          .flatMap((docPage) => docPage)
          .map(({ pageTitle, pageNumber, size }) => ({
            name: `${pageTitle ?? "Page"} ${pageNumber}`,
            pageNumber,
            filePath: renditionFileByPage.get(pageNumber),
            src: exportBanners.data?.find(
              (rendition) => rendition.pageNumber === pageNumber,
            )?.renditionUrl,
            size,
          })),
      ) ?? [];

    return [...baseAssets, ...templateAssets].map((asset) => ({
      ...asset,
      name: video.data?.customer?.name
        ? `${video.data?.customer?.name}_${asset.name}`
        : asset.name,
    }));
  }, [
    template.data,
    video.data?.assets,
    video.data?.customer?.name,
    video.data?.templateAssetsRenditionFiles,
    assetsTemplate.data?.pages,
    exportBanners.data,
  ]);

  return {
    assets,
    isLoading:
      video.isLoading || assetsTemplate.isLoading || exportBanners.isLoading,
  };
};

export const useDownloadAllAssets = (videoId) => {
  const video = useVideo(videoId);
  const template = useVideoTemplate(video.data?.templateId);
  const { assets } = useVideoAssets(videoId);

  const selectedCampaign = useMemo(
    () =>
      video.data?.campaigns?.find(
        (campaign) => campaign.id === video.data?.selectedCampaignIds?.[0],
      ),
    [video.data?.campaigns, video.data?.selectedCampaignIds],
  );

  return useMutation({
    mutationFn: async (_, { client, signal }) => {
      const isModular = video.data?.templateType === "modular";

      let texts;
      if (video.data?.templateId === "one-adobe" && selectedCampaign) {
        texts = [
          {
            name: "selected-campaign",
            data: {
              ...selectedCampaign,
              "Customer-color-primary":
                video.data?.customer?.["Brand-color"] ?? null,
              "Customer-color-secondary":
                video.data?.customer?.["Secondary-color"] ?? null,
            },
          },
        ];
      } else if (isModular) {
        try {
          const exportData = await buildModularExportJson({
            videoData: video.data,
            templateData: template.data,
            queryClient: client,
          });
          texts = [{ name: "export", data: exportData }];
        } catch (error) {
          console.debug("Failed to build modular export.json", error);
        }
      } else if (
        video.data?.templateType === "mogrt" &&
        selectedCampaign &&
        video.data?.mogrtRootPath
      ) {
        for (const exportFileName of ["export-grouped.json", "export.json"]) {
          const exportPath = `${video.data.mogrtRootPath.replace(/\/$/, "")}/${exportFileName}`;
          const s3Data = await client.query(presignURLQueryOptions(exportPath));
          const res = await fetch(s3Data.presignURL);
          if (res.status === 404 && exportFileName === "export-grouped.json") {
            continue;
          }
          if (!res.ok) {
            console.debug("Failed to fetch export template");
            break;
          }
          const exportTemplate = await res.json();
          const merged = generateExportJson(
            exportTemplate,
            selectedCampaign,
            exportFileName,
          );
          texts = [{ name: "export", data: merged }];
          break;
        }
      }

      const assetsToDownload = isModular ? (video.data?.assets ?? []) : assets;

      const stripNamePrefix = (asset) => {
        let s = String(asset.name ?? "");
        const prefix = video.data?.customer?.name;
        if (prefix && s.startsWith(prefix)) {
          s = s.slice(prefix.length).replace(/^_/, "") || s;
        }
        const pageNumber = asset.pageNumber;
        if (pageNumber != null) {
          const suffix = ` ${pageNumber}`;
          if (s.endsWith(suffix)) {
            s = s.slice(0, s.length - suffix.length);
          }
        }
        return (s || asset.name) ?? "";
      };

      const downloadName = (asset) => {
        if (isModular && asset.filePath) {
          const base = asset.filePath.split("/").pop() ?? asset.name;
          return base.replace(/\.[^./]+$/, "");
        }
        return stripNamePrefix(asset);
      };

      const images = await Promise.all(
        assetsToDownload.map((asset) =>
          handleAssetDownload(
            { ...asset, name: downloadName(asset) },
            { client, signal },
          ),
        ),
      );

      return { images, texts };
    },
    onSuccess: (data) => {
      return downloadImages(
        data.images,
        `${video.data?.name}_assets`,
        data.texts,
      );
    },
  });
};
